import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const facetMorphShapeSkill: CodeoSkill = {
    name: SKILL_NAMES.facetMorphShape,
    kind: 'codeo',
    description:
        'Reshape an SVG silhouette into a different one, so one object becomes another — for a ' +
        'metaphor that transforms, or an identity that resolves out of abstract facets.',
    documentUsesSkill: check(
        (script, markup) =>
            /morphSVG|L5Morph/.test(script) || (/<polygon\b/.test(markup) && /points/.test(script)),
    ),
    instructions: `# Facet Morph Shape

An outline travels to a different outline and the silhouette becomes something else — a cloud becoming a server, one icon turning into another, a scatter of facets resolving into a mark. Because the shape never disappears, the viewer reads *transformation* rather than replacement, which is the strongest way to carry a metaphor.

MorphSVGPlugin is injected and registered, so the two shapes do **not** need matching point counts — it resamples them for you. What it cannot do by itself is morph two shapes made of a different number of *subpaths*; the \`L5Morph\` helpers below handle that case.

## Markup

Author the target shape in a hidden \`<defs>\` and morph the visible one into it. Keeping the target as real markup means you can see it, and means the morph reads a path rather than a string you typed twice.

\`\`\`html
<div class="absolute inset-0 flex flex-col items-center justify-center gap-10 p-24">
  <svg id="morph-stage" class="overflow-visible" viewBox="0 0 600 600" style="position: relative; width: 600px; height: 600px;">
    <defs>
      <path id="shape-target" d="M300,110 C420,110 520,200 520,310 C520,430 420,500 300,500 C180,500 80,430 80,310 C80,200 180,110 300,110 Z" />
    </defs>
    <path id="shape" d="M300,60 L480,180 L480,420 L300,540 L120,420 L120,180 Z"
      fill="var(--l5-color-2, #38bdf8)" stroke="none" />
  </svg>
  <div id="morph-label" class="text-center" style="font-family: var(--l5-font-0), sans-serif; font-size: 56px; font-weight: 700; color: var(--l5-color-0, #101820)">One shape, many jobs</div>
</div>
\`\`\`

## Timeline code

Pass the target as a selector and the plugin reads its \`d\` — no duplicated path strings.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('#shape', { rotation: -12, transformOrigin: 'center center' });

tl.to('#shape', {
    morphSVG: '#shape-target',
    rotation: 0,
    duration: 1.1,
    ease: 'power3.inOut',
}, 0.8);

tl.from('#morph-label', { y: 16, opacity: 0, duration: 0.5, ease: 'power3.out' }, 1.7);

window.__timelines = { main: tl };
\`\`\`

## Shapes with different numbers of subpaths

A one-path circle becoming a three-path clock leaves two paths with nothing to morph into, and MorphSVGPlugin can only animate the pairs it is handed. \`L5Morph\` supplies the missing correspondence: it matches subpaths by position and relative area, and collapses any unpaired one to a dot at its own centre, so it grows out of or shrinks into that spot.

\`\`\`js
L5Morph.morph(tl, {
    from: '#shape-from',
    to: '#shape-to',
    stage: '#morph-stage',
    at: 0.8,
    duration: 1.1,
});
\`\`\`

That is the whole API. \`at\`, \`duration\` and \`ease\` are optional, and so is colour — each subpath keeps the fill it was authored with and tweens to the fill of the shape it becomes, so two differently-coloured shapes recolour as they morph without being told to. Pass \`fill\`/\`toFill\` only to override that.

The stage is emptied and repopulated with one path per pair, so it must be an \`<svg>\` you are willing to have cleared — not the one holding your source shapes. Primitive \`circle\`/\`rect\`/\`polygon\` shapes are converted to paths automatically, so they morph like any other.

## Adapt

- **Rotation during**: a small rotation resolving to zero, as above, makes the morph read as settling rather than as a shape being dragged.
- **Point correspondence**: if a morph turns inside out, set \`morphSVG: { shape: '#target', shapeIndex: 2 }\` — it rotates which anchor pairs with which and is the fix for edges crossing mid-morph. \`MorphSVGPlugin.convertToPath()\` on a primitive first, if you need to see its anchors.
- **Multi-step**: chaining three shapes carries a metaphor through several states — see \`${SKILL_NAMES.morphChainSpine}\` for the wider pattern of one element spanning every phase.
- **Colour with the shape**: tweening \`fill\` alongside the morph makes the transformation total; keeping the colour fixed keeps the identity and changes only the form.
- **Draw-on first**: outlining the start shape before filling it — see \`${SKILL_NAMES.svgDrawOn}\` — buys a beat before the morph without adding an element.
- **Polygon points instead**: for two shapes you are authoring yourself with the same vertex count, tweening \`attr: { points: ... }\` on a \`<polygon>\` is simpler than a morph and needs no plugin.

## Pitfalls

- **Morph between subpath counts only through \`L5Morph\`.** Handed shapes with different numbers of subpaths, MorphSVGPlugin animates the pairs it can and leaves the rest untouched, so part of the shape sits frozen through the morph — it looks like a rendering fault rather than a missing feature.
- Point the tween at a selector or a path string, not at an element you also animate separately; the plugin reads the target's \`d\` once when the tween is built.
- The path is \`rotation\`-tweened, so it must not carry a CSS \`transform\`, and its origin must be set through \`gsap.set\` — SVG elements default to the user-space origin, not their own centre.
- Concave shapes with many anchors morph unpredictably however good the pairing. Six to ten is the readable range.
- The two shapes must both be recognisable at rest. A morph between two ambiguous blobs is motion without meaning.
- Don't morph the shape while also moving it across the frame. The transformation is the event.
- The label must arrive after the morph resolves, or it names something that has not happened yet.`,
};
